import { QueryClient } from '@tanstack/react-query';

/**
 * Cliente de TanStack Query compartido por toda la app.
 *
 * Los datos del soporte cambian de un momento a otro (tickets, notificaciones,
 * asignaciones), así que se consideran "frescos" solo unos segundos y se
 * revalidan al volver a la pestaña. Lo que sí evitamos es el refetch al montar
 * si el dato aún está fresco: navegar entre pantallas ya no dispara la misma
 * petición una y otra vez.
 */
export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
            // Un 401/403/404 no se arregla reintentando: solo reintentamos
            // fallos de red o errores 5xx, y como mucho dos veces.
            retry: (intentos, error) => {
                const estado = error?.response?.status;
                if (estado && estado < 500) return false;
                return intentos < 2;
            },
        },
    },
});

/** Cabecera de autenticación con el token vigente en el momento de la llamada. */
export const cabecerasAuth = () => ({
    headers: { Authorization: localStorage.getItem('AuthToken') || '' },
});

/**
 * Claves de consulta centralizadas: tenerlas en un sitio evita que una pantalla
 * invalide con una clave y otra lea con otra distinta.
 */
export const claves = {
    ticketsUsuario: (usuario) => ['tickets', 'usuario', usuario],
    ticketsTodos: () => ['tickets', 'todos'],
    ticketsArchivados: () => ['tickets', 'archivados'],
    ticket: (identificador) => ['ticket', identificador],
    tecnicos: () => ['usuarios', 'tecnicos'],
    usuarios: () => ['usuarios', 'todos'],
    miEquipo: (usuario) => ['equipo', usuario],
    usuario: (usuario) => ['usuario', usuario],
    auditoriaUsuario: (usuario) => ['usuario', usuario, 'auditoria'],
    serviciosUsuario: (usuario) => ['usuario', usuario, 'servicios'],
    servicios: () => ['servicios'],
    estadisticasTickets: () => ['estadisticas', 'tickets'],
    notificaciones: (usuario) => ['notificaciones', usuario],
    notificacionesTickets: (usuario) => ['notificaciones', 'tickets', usuario],
    proyectos: () => ['proyectos'],
    proyectosUsuario: (usuario) => ['proyectos', 'usuario', usuario],
    proyecto: (id) => ['proyecto', id],
    equipos: () => ['equipos'],
    equipo: (id) => ['equipo', id],
    perifericos: () => ['perifericos'],
    ajustes: () => ['ajustes'],
    ajuste: (nombre) => ['ajuste', nombre],
    notificacionesAdmin: () => ['notificaciones', 'admin'],
    usuariosNotificables: () => ['notificaciones', 'admin', 'usuarios'],
    estadisticasNotificaciones: () => ['notificaciones', 'admin', 'estadisticas'],
    ticketPublico: (identificador) => ['ticket-publico', identificador],
    estadoCuenta: (usuario) => ['usuario', usuario, 'estado'],
};
